import { Link, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import { Menu, X, Phone } from "lucide-react";

const NAV_ITEMS = [
  { label: "Home", to: "/", testid: "nav-link-home" },
  { label: "Services", to: "/our-services", testid: "nav-link-services" },
  { label: "Bookkeeping", to: "/marizema-bookkeeping-service", testid: "nav-link-bookkeeping" },
  { label: "About Us", to: "/about-us", testid: "nav-link-about" },
  { label: "Tax Tips", to: "/tax-tips-blog", testid: "nav-link-blog" },
  { label: "Contact", to: "/contact", testid: "nav-link-contact" },
];

export const Logo = () => (
  <Link to="/" data-testid="nav-brand-logo" className="flex items-center gap-2.5 shrink-0">
    <img src="/icons/logo-mark.png" alt="Marizema Tax Service logo" className="h-10 w-auto" />
    <div className="leading-tight">
      <div className="font-serif-display font-semibold tracking-wide text-white text-sm sm:text-base">
        MARIZEMA <span className="text-[#D4AF37]">TAX SERVICE</span>
      </div>
      <div className="text-[10px] uppercase tracking-[0.2em] text-slate-300 hidden sm:block">
        Tax • Bookkeeping • Advisory
      </div>
    </div>
  </Link>
);

export default function Header({ onStartTax, onBookAppointment }) {
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <header
      data-testid="site-header"
      className="fixed top-0 left-0 right-0 z-50 bg-[#0B2545]/95 backdrop-blur-md border-b border-[#C9A227]/30"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between gap-3">
        <Logo />
        <nav className="hidden xl:flex items-center gap-5" aria-label="Main navigation">
          {NAV_ITEMS.map((item) => (
            <Link
              key={item.to}
              to={item.to}
              data-testid={item.testid}
              className={`whitespace-nowrap text-sm font-medium transition-colors duration-200 hover:text-[#D4AF37] ${
                location.pathname === item.to ? "text-[#D4AF37]" : "text-slate-200"
              }`}
            >
              {item.label}
            </Link>
          ))}
        </nav>
        <div className="hidden xl:flex items-center gap-3 shrink-0">
          <a data-testid="header-phone-link" href="tel:+18137280702"
            className="whitespace-nowrap flex items-center gap-1.5 text-sm font-semibold text-[#D4AF37] hover:text-white transition-colors mr-1">
            <Phone size={15} /> (813) 728-0702
          </a>
          <button
            data-testid="header-btn-book-appointment"
            onClick={onBookAppointment}
            className="whitespace-nowrap text-sm font-semibold px-4 py-2 rounded-full border border-[#C9A227]/60 text-[#D4AF37] hover:bg-[#C9A227]/10 transition-colors duration-200"
          >
            Book Appointment
          </button>
          <button
            data-testid="header-btn-start-tax"
            onClick={onStartTax}
            className="whitespace-nowrap text-sm font-semibold px-4 py-2 rounded-full bg-[#C9A227] text-[#0B2545] hover:bg-[#D4AF37] transition-colors duration-200"
          >
            Start Tax Return
          </button>
        </div>
        <button
          data-testid="nav-mobile-toggle"
          className="xl:hidden text-slate-200 p-2"
          onClick={() => setOpen(!open)}
          aria-label="Toggle menu"
        >
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>
      {open && (
        <div data-testid="nav-mobile-menu" className="xl:hidden bg-[#0B2545] border-t border-[#C9A227]/30 px-4 py-4 space-y-1">
          {NAV_ITEMS.map((item) => (
            <Link
              key={item.to}
              to={item.to}
              data-testid={`mobile-${item.testid}`}
              className={`block py-2.5 px-3 rounded text-sm font-medium ${
                location.pathname === item.to ? "text-[#D4AF37] bg-[#C9A227]/10" : "text-slate-200"
              }`}
            >
              {item.label}
            </Link>
          ))}
          <a data-testid="mobile-phone-link" href="tel:+18137280702"
            className="mt-2 flex items-center justify-center gap-2 py-2.5 rounded-full border border-[#C9A227]/60 text-[#D4AF37] text-sm font-semibold">
            <Phone size={15} /> Call (813) 728-0702
          </a>
          <div className="flex gap-2 pt-3">
            <button
              data-testid="mobile-btn-start-tax"
              onClick={() => { setOpen(false); onStartTax(); }}
              className="flex-1 text-sm font-semibold px-4 py-2.5 rounded-full bg-[#C9A227] text-[#0B2545]"
            >
              Start Tax Return
            </button>
            <button
              data-testid="mobile-btn-book-appointment"
              onClick={() => { setOpen(false); onBookAppointment(); }}
              className="flex-1 text-sm font-semibold px-4 py-2.5 rounded-full border border-[#C9A227]/60 text-[#D4AF37]"
            >
              Book Appointment
            </button>
          </div>
        </div>
      )}
    </header>
  );
}
